import React, { useEffect, useState, useRef } from 'react'
import { Box, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material'
import { Download, Print } from '@mui/icons-material'
import { QRCodeSVG } from 'qrcode.react'
import { Stage, Layer, Image, Text as KonvaText, Rect } from 'react-konva'
import logoHeader from '../../assets/images/logo-header.png'

type QRCodeDialogProps = {
	open: boolean
	primaryText: string | undefined
	secondaryText: string | undefined
	qrCodeValue: string
	onClose: () => void
	title: string
}

export default function QRCodeDialog({
	open,
	primaryText,
	secondaryText,
	qrCodeValue,
	onClose,
	title = 'QR Code',
}: QRCodeDialogProps) {
	const [qrCodeImage, setQrCodeImage] = useState<HTMLImageElement | null>(null)
	const qrCodeRef = useRef<HTMLDivElement>(null)
	const stageRef = useRef<any>(null)

	// Helper function to calculate center position for text
	const getCenteredX = (text: string, fontSize: number, containerWidth: number) => {
		// Create a temporary canvas to measure text width
		const canvas = document.createElement('canvas')
		const ctx = canvas.getContext('2d')
		if (ctx) {
			ctx.font = `${fontSize}px Arial`
			const textWidth = ctx.measureText(text).width
			return (containerWidth - textWidth) / 2
		}
		return 0
	}

	// Convert QR code SVG to image for Konva
	useEffect(() => {
		if (qrCodeValue && qrCodeRef.current) {
			const svgElement = qrCodeRef.current.querySelector('svg')
			if (svgElement) {
				const svgData = new XMLSerializer().serializeToString(svgElement)
				const canvas = document.createElement('canvas')
				const ctx = canvas.getContext('2d')
				const img = document.createElement('img') as HTMLImageElement

				img.onload = () => {
					canvas.width = img.width
					canvas.height = img.height
					ctx?.drawImage(img, 0, 0)

					const imageElement = document.createElement('img') as HTMLImageElement
					imageElement.onload = () => {
						setQrCodeImage(imageElement)
					}
					imageElement.src = canvas.toDataURL()
				}

				img.src = 'data:image/svg+xml;base64,' + btoa(svgData)
			}
		}
	}, [qrCodeValue])

	const handleClose = () => {
		setQrCodeImage(null)
		onClose()
	}

	const handlePrint = () => {
		if (stageRef.current) {
			const dataURL = stageRef.current.toDataURL()
			const printWindow = window.open('', '_blank')
			if (printWindow) {
				printWindow.document.write(`
					<html>
						<head>
							<title>QR Code - ${primaryText}</title>
							<style>
								body { margin: 0; display: flex; justify-content: center; align-items: center; min-height: 100vh; }
								img { max-width: 100%; height: auto; }
							</style>
						</head>
						<body>
							<img src="${dataURL}" alt="Pallet QR Code" />
						</body>
					</html>
				`)
				printWindow.document.close()
				printWindow.focus()
				setTimeout(() => {
					printWindow.print()
					printWindow.close()
				}, 500)
			}
		}
	}

	const handleDownload = () => {
		if (stageRef.current) {
			const dataURL = stageRef.current.toDataURL()
			const link = document.createElement('a')
			link.download = `qr-${primaryText?.slice(0, 9)}.png`
			link.href = dataURL
			document.body.appendChild(link)
			link.click()
			document.body.removeChild(link)
		}
	}

	return (
		<>
			{/* Hidden QR Code SVG for conversion */}
			<div style={{ position: 'absolute', left: '-9999px', visibility: 'hidden' }} ref={qrCodeRef}>
				<QRCodeSVG
					value={qrCodeValue}
					size={256}
					level='Q'
					imageSettings={{
						src: logoHeader,
						height: 50,
						width: 90,
						excavate: true,
					}}
				/>
			</div>

			{/* QR Code Modal with Konva Stage */}
			<Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
				<DialogTitle>{title}</DialogTitle>
				<DialogContent>
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							py: 2,
						}}
					>
						{qrCodeImage && (
							<Stage ref={stageRef} width={400} height={500}>
								<Layer>
									<Rect x={0} y={0} width={400} height={500} fill='white' />
									<Image image={qrCodeImage} width={300} height={300} x={50} y={50} />
									<KonvaText
										text={primaryText}
										x={getCenteredX(primaryText || '', 32, 400)}
										y={380}
										fontSize={32}
										fill='black'
									/>
									<KonvaText
										text={secondaryText?.slice(0, 9) ?? ''}
										x={getCenteredX(secondaryText?.slice(0, 9) ?? '', 24, 400)}
										y={430}
										fontSize={24}
										fill='black'
									/>
								</Layer>
							</Stage>
						)}
					</Box>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={handleDownload}
						variant='contained'
						color='primary'
						startIcon={<Download />}
					>
						Download
					</Button>
					<Button
						onClick={handlePrint}
						variant='outlined'
						color='primary'
						sx={{ color: 'primary.main' }}
						startIcon={<Print />}
					>
						Print
					</Button>
					<Button onClick={handleClose} variant='contained' color='warning'>
						Close
					</Button>
				</DialogActions>
			</Dialog>
		</>
	)
}
